import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/** Every step, in order. Changing a choice clears the choices after it. */
const ORDER = ['product', 'printed', 'size', 'pack', 'position', 'upload', 'stakes', 'qty'];
/** The steps whose tiles come from Shopify, with the product option each one stands for. */
const OPTION_STEPS = /** @type {const} */ (['size', 'pack', 'position']);
const VECTOR_TYPES = ['svg', 'ai', 'eps', 'pdf'];
const RASTER_TYPES = ['png', 'jpg', 'jpeg'];

/**
 * @typedef {object} Choice
 * @property {string} value - The name shown to the customer.
 * @property {string} [id] - The Shopify option value id, for size, pack and print position.
 * @property {string} [url] - The product URL, for a product tile.
 * @property {string} [title] - The product title, for a product tile.
 * @property {string} [unit] - "pack" when the product is sold in packs or sets, else "item".
 * @property {boolean} [group] - True for the "Printed Products" tile, which opens a second step.
 * @property {boolean} [stakes] - True when the yard stakes add-on is offered with the product.
 * @property {boolean} [engraving] - True when the product is engraved and wants a vector file.
 */

/**
 * One artwork file field. Double sided printing has two of these.
 * @typedef {object} Upload
 * @property {string} property - The line item property name, for example "Front artwork".
 * @property {HTMLElement} element
 * @property {HTMLInputElement} input
 * @property {boolean} vectorize - True when the service was added for this file.
 */

/**
 * @typedef {object} SignsBuilderRefs
 * @property {HTMLFormElement} form
 * @property {HTMLElement} [fallback]
 * @property {HTMLElement} chips
 * @property {HTMLElement} [empty]
 * @property {HTMLElement} price
 * @property {HTMLElement} [priceNote]
 * @property {HTMLElement} stage
 * @property {HTMLElement} progress
 * @property {HTMLElement} status
 * @property {HTMLElement} uploads
 * @property {HTMLTemplateElement} uploadTemplate
 * @property {HTMLElement} [engravingNotice]
 * @property {HTMLInputElement} [stakesInput]
 * @property {HTMLElement} [stakesLine]
 * @property {HTMLInputElement} quantity
 * @property {HTMLTextAreaElement} notes
 * @property {HTMLElement} total
 * @property {HTMLTemplateElement} [moneyFormat]
 * @property {HTMLButtonElement} submit
 * @property {HTMLButtonElement} back
 * @property {HTMLButtonElement} next
 * @property {HTMLElement} fields
 */

/**
 * Step-by-step Signs & Banners builder, modeled on the hat and apparel builders but kept
 * separate from them. Sizes, pack quantities, print positions, prices and variant ids are
 * read from sections/signs-builder-product.liquid through the Section Rendering API, so a
 * combination with no variant is never offered and never sent to the cart.
 *
 * @extends {Component<SignsBuilderRefs>}
 */
class SignsBuilder extends Component {
  requiredRefs = [
    'form', 'chips', 'price', 'stage', 'progress', 'status', 'uploads', 'uploadTemplate',
    'quantity', 'notes', 'total', 'submit', 'back', 'next', 'fields',
  ];

  /** @type {Record<string, Choice | undefined>} */
  #state = {};
  #current = 0;
  /** Option value ids sent to Shopify to resolve variants. */
  #ids = { size: '', pack: '', position: '' };
  /** Which option steps the chosen product has. */
  #has = { size: false, pack: false };
  /** The variant for the chosen size, pack and print position. @type {{ id: string, price: string, cents: number } | null} */
  #variant = null;
  /** @type {Upload[]} */
  #uploads = [];
  #quantity = 1;
  /** Packs of yard stakes; 0 adds none. */
  #stakes = 0;
  #busy = false;
  #sending = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #sendingTimer;
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {AbortController | undefined} */
  #request;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const { form, fallback } = this.refs;

    this.addEventListener('click', this.#onClick, { signal });
    form.addEventListener('submit', this.#onSubmit, { signal });
    form.addEventListener('change', this.#onChange, { signal });
    form.addEventListener('input', this.#onInput, { signal });
    // Coming back with the browser's back button: allow sending again.
    window.addEventListener('pageshow', () => this.#setSending(false), { signal });

    if (fallback) fallback.hidden = true;
    form.hidden = false;
    this.#decorateGroupTiles();
    this.#render(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    clearTimeout(this.#sendingTimer);
  }

  /** The "Printed Products" tile borrows the photo of its first product, once there is one. */
  #decorateGroupTiles() {
    for (const tile of this.#tiles('product')) {
      const group = tile.dataset.group;
      const placeholder = tile.querySelector('.signs-builder__placeholder');
      const image = group ? this.#tiles(group).find((candidate) => candidate.querySelector('img'))?.querySelector('img') : null;
      if (placeholder && image) placeholder.replaceWith(image.cloneNode(true));
    }
  }

  /**
   * @param {string} key
   * @returns {HTMLElement | null}
   */
  #panel(key) {
    return this.querySelector(`[data-panel="${key}"]`);
  }

  /**
   * @param {string} key
   * @returns {HTMLElement[]} Every tile of a step, shown or not.
   */
  #tiles(key) {
    return Array.from(this.querySelectorAll(`.hat-builder__tile[data-step="${key}"]`));
  }

  /** @returns {Choice | undefined} The product being built: the printed product, or the product tile itself. */
  #model() {
    const { product, printed } = this.#state;
    return printed ?? (product?.url ? product : undefined);
  }

  /** @returns {boolean} True when the chosen print position prints both sides. */
  #doubleSided() {
    return (this.#state.position?.value ?? '').toLowerCase().includes('double');
  }

  /**
   * @param {HTMLElement} tile
   * @returns {boolean} True when the tile can still lead to a variant that can be bought.
   */
  #purchasable(tile) {
    // Print position is the last option, so its tile is one exact variant.
    if (tile.dataset.step === 'position') return Boolean(tile.dataset.variantId) && tile.dataset.variantAvailable === 'true';
    return Boolean(tile.dataset.lowestCents);
  }

  /**
   * The tiles a customer may pick on a step.
   * @param {string} key
   * @returns {HTMLElement[]}
   */
  #choices(key) {
    const tiles = this.#tiles(key);
    // A size, pack or print position with nothing to buy for the choices so far is left out.
    return key === 'product' || key === 'printed' ? tiles : tiles.filter((tile) => this.#purchasable(tile));
  }

  /** @returns {string[]} The steps that apply to the choices so far. */
  #steps() {
    const model = this.#model();
    return ORDER.filter((key) => {
      if (key === 'printed') return Boolean(this.#state.product?.group);
      if (key === 'size') return !model || this.#has.size;
      if (key === 'pack') return Boolean(model) && this.#has.pack;
      if (key === 'stakes') return Boolean(model?.stakes) && Boolean(this.refs.stakesInput);
      return true;
    });
  }

  /**
   * @param {string} key
   * @returns {boolean} The stakes and the quantity always have a value; every other step needs an answer.
   */
  #answered(key) {
    return key === 'stakes' || key === 'qty' || Boolean(this.#state[key]);
  }

  /** @param {string} key */
  #clearAfter(key) {
    for (const later of ORDER.slice(ORDER.indexOf(key) + 1)) delete this.#state[later];
    this.#variant = null;
    this.#uploads = [];
    this.refs.uploads.replaceChildren();
    // A different product starts again at one, with no stakes.
    if (key === 'product' || key === 'printed') {
      this.#quantity = 1;
      this.refs.quantity.value = '1';
      this.#stakes = 0;
      if (this.refs.stakesInput) this.refs.stakesInput.value = '0';
    }
  }

  /** @param {HTMLElement} tile */
  async #choose(tile) {
    const key = tile.dataset.step;
    const value = tile.dataset.value;
    if (this.#busy || !key || !value) return;

    const changed = this.#state[key]?.value !== value;
    if (changed) {
      this.#clearAfter(key);
      const data = tile.dataset;
      this.#state[key] = {
        value,
        id: data.optionValueId,
        url: data.url,
        title: data.title,
        unit: data.unit,
        group: Boolean(data.group),
        stakes: data.stakes === 'true',
        engraving: data.engraving === 'true',
      };

      try {
        if ((key === 'product' || key === 'printed') && data.url) await this.#loadModel();
        if (key === 'size' || key === 'pack') {
          this.#ids[key] = data.optionValueId ?? '';
          await this.#refreshOptions();
          const next = key === 'size' && this.#has.pack ? 'pack' : 'position';
          if (!this.#choices(next).length) throw new Error('unavailable');
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        delete this.#state[key];
        this.#setBusy(false);
        this.#showStatus(error instanceof Error && error.message === 'unavailable' ? 'unavailable' : 'error');
        this.#render(false);
        return;
      }

      if (key === 'position') {
        this.#ids.position = data.optionValueId ?? '';
        this.#variant = { id: data.variantId ?? '', price: data.price ?? '', cents: Number(data.priceCents) || 0 };
        this.#buildUploads();
      }
    }

    this.#showStatus('');
    const steps = this.#steps();
    this.#current = Math.min(steps.indexOf(key) + 1, steps.length - 1);
    this.#render(true);
  }

  /**
   * Ask Shopify for the product's option tiles, resolved for the given option values.
   * @param {string[]} ids
   * @returns {Promise<HTMLElement>}
   */
  async #fetchOptions(ids) {
    const model = this.#model();
    if (!model?.url) throw new Error('error');

    this.#request?.abort();
    this.#request = new AbortController();
    this.#setBusy(true);

    const url = new URL(model.url, window.location.origin);
    url.searchParams.set('section_id', this.dataset.section ?? '');
    const selected = ids.filter(Boolean);
    if (selected.length) url.searchParams.set('option_values', selected.join(','));

    const response = await fetch(url, { signal: this.#request.signal });
    if (!response.ok) throw new Error('error');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const root = html.querySelector('.signs-builder-product');
    if (!(root instanceof HTMLElement)) throw new Error('error');

    this.#setBusy(false);
    return root;
  }

  /**
   * @param {HTMLElement} root
   * @param {string} option - "size", "pack" or "position".
   * @returns {HTMLElement[]}
   */
  #optionTiles(root, option) {
    return /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll(`[data-option="${option}"] .hat-builder__tile`)));
  }

  /**
   * Replace the option steps' tiles with the ones Shopify rendered, and write their prices.
   * @param {HTMLElement} root
   */
  #fill(root) {
    for (const key of OPTION_STEPS) {
      const box = this.querySelector(`[data-tiles="${key}"]`);
      box?.replaceChildren(...this.#optionTiles(root, key).map((node) => document.importNode(node, true)));
    }
    this.#annotate();
  }

  /**
   * Writes each tile's price. A size shows "from" when a pack is still to be chosen. A print
   * position shows its exact price and what it adds to the first one, read from the variants.
   */
  #annotate() {
    const data = this.dataset;
    const note = (/** @type {HTMLElement} */ tile, /** @type {string} */ selector, /** @type {string} */ text) => {
      const element = tile.querySelector(selector);
      if (!(element instanceof HTMLElement)) return;
      element.textContent = text;
      element.hidden = !text;
    };

    for (const tile of this.#tiles('size')) {
      const price = tile.dataset.lowestPrice ?? '';
      note(tile, '[data-note]', price && this.#has.pack ? (data.fromLabel ?? '[price]').replace('[price]', price) : price);
    }
    for (const tile of this.#tiles('pack')) note(tile, '[data-note]', tile.dataset.lowestPrice ?? '');

    const positions = this.#tiles('position').filter((tile) => this.#purchasable(tile));
    const base = Number(positions[0]?.dataset.priceCents) || 0;
    positions.forEach((tile, index) => {
      const extra = (Number(tile.dataset.priceCents) || 0) - base;
      note(tile, '[data-note]', tile.dataset.price ?? '');
      let text = '';
      if (index > 0 && extra > 0) text = (data.extraLabel ?? '+[price]').replace('[price]', this.#money(extra));
      if (index > 0 && extra === 0) text = data.noExtraLabel ?? '';
      note(tile, '[data-extra]', text);
    });
  }

  /**
   * @param {HTMLElement} root
   * @param {string} option
   * @returns {string} The id of the option value Shopify treats as selected.
   */
  #selectedId(root, option) {
    return this.#optionTiles(root, option).find((tile) => tile.dataset.selected === 'true')?.dataset.optionValueId ?? '';
  }

  async #loadModel() {
    const root = await this.#fetchOptions([]);
    if (!this.#optionTiles(root, 'position').length) throw new Error('error');

    this.#has = { size: this.#optionTiles(root, 'size').length > 0, pack: this.#optionTiles(root, 'pack').length > 0 };
    this.#ids = {
      size: this.#selectedId(root, 'size'),
      pack: this.#selectedId(root, 'pack'),
      position: this.#selectedId(root, 'position'),
    };
    this.#fill(root);
    const first = this.#has.size ? 'size' : this.#has.pack ? 'pack' : 'position';
    if (!this.#choices(first).length) throw new Error('unavailable');
  }

  /** Reloads the tiles for the size and pack chosen so far. All three ids are always sent. */
  async #refreshOptions() {
    const { size, pack, position } = this.#ids;
    this.#fill(await this.#fetchOptions([size, pack, position]));
  }

  /** One artwork field for single sided printing, a front and a back for double sided. */
  #buildUploads() {
    const { uploads, uploadTemplate, quantity } = this.refs;
    const data = this.dataset;
    const sides = this.#doubleSided()
      ? [
          { label: data.frontLabel ?? '', property: 'Front artwork' },
          { label: data.backLabel ?? '', property: 'Back artwork' },
        ]
      : [{ label: data.artworkLabel ?? '', property: 'Artwork' }];

    this.#uploads = [];
    const elements = sides.map((side, index) => {
      const element = /** @type {HTMLElement} */ (uploadTemplate.content.firstElementChild?.cloneNode(true));
      const input = /** @type {HTMLInputElement} */ (element.querySelector('[data-upload-input]'));
      const label = element.querySelector('[data-upload-label]');
      input.id = `${quantity.id}-upload-${index}`;
      if (label instanceof HTMLLabelElement) {
        label.textContent = side.label || side.property;
        label.htmlFor = input.id;
      }
      element.dataset.upload = String(index);
      this.#uploads.push({ property: side.property, element, input, vectorize: false });
      return element;
    });
    uploads.replaceChildren(...elements);
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string} The chosen file's extension in lower case, or '' when no file is chosen.
   */
  #extension(input) {
    const file = input.files?.[0];
    return file ? (file.name.split('.').pop() ?? '').toLowerCase() || 'unknown' : '';
  }

  /** @param {string} type */
  #accepted(type) {
    return VECTOR_TYPES.includes(type) || RASTER_TYPES.includes(type);
  }

  /** Works out whether the upload step is answered, and what its chip says. */
  #syncUpload() {
    const complete = this.#uploads.length > 0 && this.#uploads.every((upload) => this.#accepted(this.#extension(upload.input)));
    this.#state.upload = complete ? { value: this.#uploads.map((upload) => upload.input.files?.[0]?.name ?? '').join(', ') } : undefined;
  }

  /** @returns {Upload[]} The raster files the customer asked to have vectorized: one service line each. */
  #services() {
    if (!this.dataset.serviceVariantId) return [];
    return this.#uploads.filter((upload) => upload.vectorize && RASTER_TYPES.includes(this.#extension(upload.input)));
  }

  /** @returns {number} Packs of stakes to add, 0 when the add-on does not apply. */
  #stakesPacks() {
    return this.#steps().includes('stakes') && this.dataset.stakesVariantId ? this.#stakes : 0;
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.refs.stage.setAttribute('aria-busy', String(busy));
    if (busy) this.#showStatus('loading');
  }

  /**
   * Blocks a second send while the first is uploading. If the browser never leaves the
   * page (for example the connection dropped), sending is allowed again after a while.
   * @param {boolean} sending
   */
  #setSending(sending) {
    clearTimeout(this.#sendingTimer);
    this.#sending = sending;
    this.#showStatus(sending ? 'sending' : '');
    if (sending) this.#sendingTimer = setTimeout(() => this.#setSending(false), 30000);
  }

  /** @param {'' | 'loading' | 'error' | 'unavailable' | 'sending'} kind */
  #showStatus(kind) {
    const { status } = this.refs;
    status.textContent = kind ? status.dataset[kind] ?? '' : '';
    status.hidden = !kind;
  }

  /** @returns {boolean} True only when every step is answered and the choices are a real variant. */
  #ready() {
    return this.#steps().every((key) => this.#answered(key)) && Boolean(this.#variant?.id) && this.#quantity >= 1 && !this.#busy;
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.refs.moneyFormat?.content.textContent || '{{amount}}';
    return formatMoney(cents, format, this.dataset.currency ?? '');
  }

  /** @param {boolean} moved - True when the step changed, to slide it in and move focus. */
  #render(moved) {
    const state = this.#state;
    const refs = this.refs;
    const model = this.#model();
    this.#syncUpload();

    const steps = this.#steps();
    this.#current = Math.min(this.#current, steps.length - 1);
    const key = steps[this.#current] ?? 'product';

    for (const panel of this.querySelectorAll('[data-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      const active = panel.dataset.panel === key;
      panel.hidden = !active;
      panel.removeAttribute('data-enter');
      if (active && moved) {
        // Restart the slide-in animation.
        void panel.offsetWidth;
        panel.setAttribute('data-enter', '');
        const heading = panel.querySelector('.signs-builder__panel-heading');
        if (heading instanceof HTMLElement) heading.focus();
      }
    }

    for (const step of ['product', 'printed', ...OPTION_STEPS]) {
      const allowed = new Set(this.#choices(step));
      for (const tile of this.#tiles(step)) {
        tile.hidden = !allowed.has(tile);
        tile.setAttribute('aria-pressed', String(state[step]?.value === tile.dataset.value));
      }
    }

    this.#renderUploads();
    if (refs.engravingNotice) refs.engravingNotice.hidden = !model?.engraving;
    for (const help of this.querySelectorAll('[data-unit-for]')) {
      if (help instanceof HTMLElement) help.hidden = help.dataset.unitFor !== (model?.unit ?? 'item');
    }

    refs.progress.replaceChildren(
      ...steps.map((step) => {
        const segment = document.createElement('span');
        const passed = this.#current > steps.indexOf(step);
        if (step === 'stakes' || step === 'qty' ? passed : state[step]) segment.setAttribute('data-done', '');
        return segment;
      })
    );

    // The exact variant price for the chosen size, pack and print position.
    refs.price.textContent = this.#variant?.price || '—';

    this.#updateTotals();
    refs.back.disabled = this.#current === 0;
    refs.next.disabled = !this.#answered(key) || this.#current >= steps.length - 1;
  }

  /** Shows the right messages under each artwork file. */
  #renderUploads() {
    const refs = this.refs;
    const hasService = Boolean(this.dataset.serviceVariantId);

    for (const upload of this.#uploads) {
      const type = this.#extension(upload.input);
      const raster = RASTER_TYPES.includes(type);
      const show = (/** @type {string} */ selector, /** @type {boolean} */ visible) => {
        const element = upload.element.querySelector(selector);
        if (element instanceof HTMLElement) element.hidden = !visible;
      };
      show('[data-upload-vector]', VECTOR_TYPES.includes(type));
      show('[data-upload-raster]', raster);
      show('[data-upload-type]', Boolean(type) && !this.#accepted(type));
      show('[data-upload-vectorize]', raster && hasService);
      show('[data-upload-notice]', raster && hasService);
      const toggle = upload.element.querySelector('[data-upload-vectorize]');
      if (toggle instanceof HTMLElement) this.#setToggle(toggle, upload.vectorize);
    }

    if (refs.priceNote) {
      const data = this.dataset;
      refs.priceNote.textContent = (this.#services().length > 0 ? data.priceNoteVectorization : data.priceNote) ?? '';
    }
  }

  /**
   * @param {HTMLElement} toggle
   * @param {boolean} on
   */
  #setToggle(toggle, on) {
    toggle.setAttribute('aria-pressed', String(on));
    for (const label of toggle.querySelectorAll('span')) label.hidden = label.hasAttribute('data-on') !== on;
  }

  /** The customer's choices so far. Tapping one goes back to that step. */
  #renderChips() {
    const steps = this.#steps();
    const packs = this.#stakesPacks();
    const chips = [];
    for (const key of steps) {
      let text = this.#state[key]?.value ?? '';
      if (key === 'stakes') text = packs > 0 ? String(packs) : '';
      // The quantity shows once the customer has reached that step.
      if (key === 'qty') text = this.#variant && this.#current === steps.length - 1 ? String(this.#quantity) : '';
      if (!text) continue;

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'signs-builder__chip';
      chip.dataset.chip = key;

      const label = document.createElement('span');
      label.className = 'signs-builder__chip-label';
      label.textContent = this.#panel(key)?.dataset.label ?? '';
      const value = document.createElement('span');
      value.className = 'signs-builder__chip-value';
      value.textContent = text;
      chip.append(label, value);
      chips.push(chip);
    }
    this.refs.chips.replaceChildren(...chips);
    if (this.refs.empty) this.refs.empty.hidden = chips.length > 0;
  }

  /** Refreshes the stakes line, the total, the selections and the Add to cart button. */
  #updateTotals() {
    const { total, submit, stakesLine } = this.refs;
    const data = this.dataset;
    const packs = this.#stakesPacks();
    const stakesCents = packs * (Number(data.stakesCents) || 0);

    if (stakesLine) {
      const count = this.#stakes * (Number(data.stakesPerPack) || 0);
      stakesLine.textContent =
        this.#stakes > 0
          ? (data.stakesLine ?? '').replace('[count]', String(count)).replace('[price]', this.#money(this.#stakes * (Number(data.stakesCents) || 0)))
          : '';
      stakesLine.hidden = this.#stakes === 0;
    }

    const priced = Boolean(this.#variant?.id);
    const sum = priced ? this.#money((this.#variant?.cents ?? 0) * this.#quantity + stakesCents) : '';
    total.textContent = sum || '—';
    submit.textContent = (data.addLabel ?? '').replace('[price]', sum).replace(/[\s-]+$/, priced ? '$&' : '');
    submit.disabled = !this.#ready();
    this.#renderChips();
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const { back, next } = this.refs;

    const tile = target.closest('.hat-builder__tile');
    if (tile instanceof HTMLElement && this.contains(tile)) {
      this.#choose(tile);
      return;
    }

    const chip = target.closest('.signs-builder__chip');
    if (chip instanceof HTMLElement && !this.#busy) {
      this.#current = Math.max(0, this.#steps().indexOf(chip.dataset.chip ?? ''));
      this.#render(true);
      return;
    }

    if (back.contains(target) || next.contains(target)) {
      if (this.#busy) return;
      this.#current = Math.min(this.#steps().length - 1, Math.max(0, this.#current + (back.contains(target) ? -1 : 1)));
      this.#render(true);
      return;
    }

    const vectorize = target.closest('[data-upload-vectorize]');
    const group = vectorize?.closest('[data-upload]');
    if (vectorize && group instanceof HTMLElement) {
      const upload = this.#uploads[Number(group.dataset.upload)];
      if (upload) upload.vectorize = !upload.vectorize;
      this.#render(false);
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    const { quantity, stakesInput } = this.refs;
    // Leaving a number field: show the number that will be used.
    if (target === quantity) quantity.value = String(this.#quantity);
    if (stakesInput && target === stakesInput) stakesInput.value = String(this.#stakes);

    if (target instanceof HTMLInputElement && target.type === 'file') {
      // A new file starts without the service.
      const upload = this.#uploads.find((candidate) => candidate.input === target);
      if (upload) upload.vectorize = false;
      this.#render(false);
    }
  };

  /**
   * Typing a quantity updates the total straight away.
   * @param {Event} event
   */
  #onInput = (event) => {
    const { quantity, stakesInput } = this.refs;
    if (event.target === quantity) {
      this.#quantity = Math.max(1, Math.floor(Number(quantity.value)) || 1);
      this.#updateTotals();
    } else if (stakesInput && event.target === stakesInput) {
      this.#stakes = Math.max(0, Math.floor(Number(stakesInput.value)) || 0);
      this.#updateTotals();
    }
  };

  /**
   * Writes the cart fields, then lets the browser post the form (multipart, so the files
   * are uploaded as line item properties). Shopify answers by showing the cart.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    if (!this.#ready() || this.#sending || !this.#variant) {
      event.preventDefault();
      return;
    }

    const data = this.dataset;
    const services = this.#services();
    const packs = this.#stakesPacks();
    const title = this.#model()?.title ?? '';
    // The product alone uses Shopify's plain product fields. Stakes or a service make it a list of "items".
    const list = packs > 0 || services.length > 0;
    const field = (/** @type {string} */ name) => (list ? `items[0][${name}]` : name);
    const property = (/** @type {string} */ name) => (list ? `items[0][properties][${name}]` : `properties[${name}]`);

    /** @type {[string, string][]} */
    const values = [
      [field('id'), this.#variant.id],
      [field('quantity'), String(this.#quantity)],
    ];
    const notes = this.refs.notes.value.trim();
    if (notes) values.push([property(this.refs.notes.dataset.property ?? 'Notes'), notes]);

    let index = 1;
    // The yard stakes are their own cart line.
    if (packs > 0) {
      values.push(
        [`items[${index}][id]`, data.stakesVariantId ?? ''],
        [`items[${index}][quantity]`, String(packs)],
        [`items[${index}][properties][For]`, title]
      );
      index += 1;
    }
    // One service line per file that needs it.
    for (const upload of services) {
      values.push(
        [`items[${index}][id]`, data.serviceVariantId ?? ''],
        [`items[${index}][quantity]`, '1'],
        [`items[${index}][properties][For]`, `${title} - ${upload.property}`]
      );
      index += 1;
    }

    // The files ride on the product's line.
    for (const upload of this.#uploads) upload.input.name = property(upload.property);
    // The comment box is sent as a line item property, not under its own name.
    this.refs.fields.replaceChildren(
      ...values.map(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        return input;
      })
    );
    this.#setSending(true);
  };
}

if (!customElements.get('signs-builder')) {
  customElements.define('signs-builder', SignsBuilder);
}
